import React, {useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Checkbox} from 'antd';
import {
  StyledTermsAcceptanceModal,
  StyledTermsAcceptanceModalButton,
  StyledTermsAcceptanceModalSection,
  StyledTermsAcceptanceModalSpace
} from './StyledTermsAcceptanceModal';
import SpinSmall from '../../SpinSmall';
import {userActions} from '../../../state/actions/user.actions';
import {useCurrentUser, useEmployee, useIsFeaturesLoaded, useIsKnownEmployee, useIsTreezorEnabled} from '../../../hooks';
import {userRolesConstants, TERM_OF_USE_URL} from '../../../constants';
import {systemHelpers} from '../../../utils/helpers';

/**
 * `null` means "deployed and not yet accepted"; **absent** means the backend that records this is
 * not there at all. They are not the same thing and must not be conflated.
 *
 * B2 (H1MVP-4649) adds these fields. If this modal shipped somewhere B2 had not, every field would
 * read as undefined, every check would look like "not accepted", and we would prompt the entire user
 * base with nowhere to write the answer. Requiring the key to exist makes that case render nothing.
 */
const pendingAcceptance = (source, key) =>
  Boolean(source) && Object.prototype.hasOwnProperty.call(source, key) && source[key] === null;

/**
 * Ask the client to accept the terms, once, on their own login.
 *
 * This is a UX gate, not an authorization gate (decision D6). It is bypassable by a second tab, a
 * direct URL, the mobile app or curl, and that is accepted — the requirement is that acceptance is
 * *recorded*, not that anyone is prevented from proceeding. Do not describe it as blocking.
 *
 * The two sections are independent and come from different endpoints, because the facts they record
 * live at different levels (CC-5):
 *
 *   platform → `users/current`          → per LOGIN   → a person in three companies accepts once
 *   banking  → `users/employee/current` → per COMPANY → one Admin's click binds their colleagues
 *
 * ⚠ Roles are compared to `userRolesConstants` directly rather than through `teamsHelpers`.
 * `teamsHelpers.isAdmin` returns true for BOOKKEEPER and SUPPORT as well as ADMIN, so using it here
 * would offer the Treezor terms to the sales rep — which is the exact failure this story exists to
 * fix, since the rep is not the party entitled to accept on the client's behalf.
 */
const TermsAcceptanceModal = () => {
  const [trans] = useTranslation(['quickStart', 'main']);
  const dispatch = useDispatch();

  const user = useCurrentUser();
  const employee = useEmployee();
  // Set alongside isAdmin/isSupport in the GET_EMPLOYEE_PROFILE case. Until it is true, `role` is
  // undefined and any role test that passes on undefined is answering a question it cannot answer.
  const isKnownEmployee = useIsKnownEmployee();
  const companyCountry = useSelector(state => state.company?.country);

  const isFeaturesLoaded = useIsFeaturesLoaded();
  const isTreezorEnabled = useIsTreezorEnabled();

  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState({platform: false, banking: false});

  const t = (key, options) => trans(`terms.${key}`, options);

  const role = employee?.role;

  // ⚠ This is a NEGATIVE role test, so it passes while the role is still unknown. `SystemModals`
  // mounts the moment `isAuth` flips, and isAuth is set by the same dispatch that fills
  // `state.user.user` — while `state.user.employee` waits on a later call. For that whole window
  // `role` is undefined, `undefined !== SUPPORT` is true, and a support rep was shown platform
  // terms they can never accept, on every single page load (H1MVP-4787).
  //
  // `showBanking` below needs no such gate, and not by luck: it tests `role === ADMIN`, and a
  // POSITIVE test is already false while the role is unknown. That is the difference worth keeping
  // in mind here — not that one has more guards, but that one asks a question undefined can pass.
  const showPlatform = isKnownEmployee
    && pendingAcceptance(user, 'platform_terms_accepted_at')
    && role !== userRolesConstants.SUPPORT;

  // ⚠ `useIsTreezorEnabled` is `!useCompanyFeatureAvailable(...)`, and that hook answers false while
  // the feature list is still loading — so treezor reads as ENABLED before load. Without the
  // isFeaturesLoaded gate this section would flash for a company that has banking switched off.
  const showBanking = pendingAcceptance(employee, 'banking_terms_accepted_at')
    && role === userRolesConstants.ADMIN
    && isFeaturesLoaded
    && isTreezorEnabled;

  const trzTermsUrl = useMemo(
    // `state.company.country` is already an ISO alpha-2 code — the reducer converts the name on the
    // way in — which is the shape getTrzTermsUrl looks up. Passing a country *name* would silently
    // fall through to the default (English) terms for every Italian and French company.
    () => systemHelpers.getTrzTermsUrl(companyCountry),
    [companyCountry]
  );

  const termsKeys = useMemo(() => [
    ...(showPlatform ? ['platform'] : []),
    ...(showBanking ? ['banking'] : []),
  ], [showPlatform, showBanking]);

  if (termsKeys.length === 0) return null;

  const allChecked = termsKeys.every(key => accepted[key]);

  const onSubmit = () => {
    setLoading(true);
    dispatch(userActions.updateEmployeePopupSettings({
      // Only the sections actually shown. The server re-checks the role per key and 403s a `banking`
      // key from a non-Admin, so sending one speculatively would fail the whole request.
      data: {terms_accepted: termsKeys},
      successCallback: () => {
        // Re-read the profiles rather than closing optimistically. The modal's visibility is derived
        // from those timestamps, so it closes when — and only when — the write is actually recorded.
        // Dismissing on click instead would hide an acceptance that never persisted.
        dispatch(userActions.getUserProfile(true, false));
        dispatch(userActions.getEmployeeProfile());
        setLoading(false);
      },
      errorCallback: () => setLoading(false)
    }));
  }

  return (
    <StyledTermsAcceptanceModal
      closeIcon={false}
      footer={null}
      maskClosable={false}
      open={true}
      title={t('title')}
      width={520}
    >
      <SpinSmall spinning={loading}>
        <StyledTermsAcceptanceModalSpace
          direction='vertical'
          size={20}
        >
          <p>{t('description')}</p>

          {showPlatform && (
            <StyledTermsAcceptanceModalSection>
              <Checkbox
                checked={accepted.platform}
                onChange={(e) => setAccepted(prev => ({...prev, platform: e.target.checked}))}
              >
                {t('platform.label')}{' '}
                <a
                  href={TERM_OF_USE_URL}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {t('platform.linkText')}
                </a>
              </Checkbox>
            </StyledTermsAcceptanceModalSection>
          )}

          {showBanking && (
            <StyledTermsAcceptanceModalSection>
              <Checkbox
                checked={accepted.banking}
                onChange={(e) => setAccepted(prev => ({...prev, banking: e.target.checked}))}
              >
                {t('banking.label')}{' '}
                <a
                  href={trzTermsUrl}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {t('banking.linkText')}
                </a>
              </Checkbox>
            </StyledTermsAcceptanceModalSection>
          )}

          <StyledTermsAcceptanceModalButton
            disabled={!allChecked || loading}
            onClick={onSubmit}
            size='large'
            type='primary'
        >
            {t('submit')}
          </StyledTermsAcceptanceModalButton>
        </StyledTermsAcceptanceModalSpace>
      </SpinSmall>
    </StyledTermsAcceptanceModal>
  );
}

export default TermsAcceptanceModal;
